import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useToast } from '../../components/shared/ToastNotification';
import api from '../../services/api';

interface DeliveryLog {
  id: string;
  customer: { 
    id: string; 
    name: string; 
    phone: string; 
    google_maps_url?: string; 
    latitude?: number; 
    longitude?: number;
    billing_address?: string;
  };
  product: { id: string; name: string };
  quantity_delivered: number;
  status: string;
  delivery_time?: string;
}

export default function PartnerChecklist() {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const [logs, setLogs] = useState<DeliveryLog[]>([]);
  const [loading, setLoading] = useState(true);
  const { success, error } = useToast();

  useEffect(() => {
    fetchLogs();
  }, [assignmentId]);

  const fetchLogs = async () => {
    try {
      const res = await api.get(`/delivery_assignments/${assignmentId}/delivery_logs`);
      if (res.success) {
        setLogs(res.data.delivery_logs || []);
      } else {
        error(res.message || 'Failed to load checklist');
      }
    } catch (err) {
      error('Failed to load checklist');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (logId: string, status: string) => {
    try {
      const res = await api.patch(`/delivery_assignments/${assignmentId}/delivery_logs/${logId}`, { 
        delivery_log: { status, delivery_time: new Date() } 
      });
      if (res.success) {
        success(`Marked as ${status}`);
        fetchLogs();
      } else {
        error(res.message || 'Update failed');
      }
    } catch (err) {
      error('An error occurred');
    }
  };

  const openInMaps = (log: DeliveryLog) => {
    if (log.customer.google_maps_url) {
      window.open(log.customer.google_maps_url, '_blank');
    } else if (log.customer.latitude && log.customer.longitude) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${log.customer.latitude},${log.customer.longitude}`, '_blank');
    } else {
      error('No location stored for this customer');
    }
  };

  return (
    <div className="max-w-md mx-auto space-y-6 pb-20">
      <div className="mb-6">
        <h1 className="text-2xl font-black text-slate-800 tracking-tight">My Checklist</h1>
        <p className="text-slate-500 font-bold text-xs uppercase tracking-widest mt-1">Today's Delivery Batch</p>
      </div>

      <div className="space-y-4">
        {loading ? (
          <div className="py-10 text-center text-slate-400">Loading your route...</div>
        ) : logs.length === 0 ? (
          <div className="py-10 text-center text-slate-400">No deliveries in this batch.</div>
        ) : (
          logs.map((log) => (
            <div key={log.id} className={`bg-white p-4 rounded-3xl border border-slate-200 shadow-sm transition-all ${log.status !== 'pending' ? 'opacity-60 grayscale-[0.5]' : ''}`}>
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-2xl flex items-center justify-center font-bold text-lg select-none ${log.status === 'delivered' ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-indigo-600'}`}>
                    {log.customer.name.charAt(0)}
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800 tracking-tight leading-tight">{log.customer.name}</h3>
                    <p className="text-[10px] font-black text-indigo-600 uppercase tracking-widest">{log.product.name} • {log.quantity_delivered}L</p>
                  </div>
                </div>
                <button 
                  onClick={() => openInMaps(log)}
                  className="p-2.5 bg-indigo-50 text-indigo-600 rounded-2xl hover:bg-indigo-600 hover:text-white transition-all flex items-center justify-center"
                  title="Navigate"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>
              </div>

              {log.customer.billing_address && (
                <p className="text-xs text-slate-400 mb-4 line-clamp-1 italic px-1">{log.customer.billing_address}</p>
              )}

              {log.status === 'pending' ? (
                <div className="grid grid-cols-2 gap-2">
                  <button 
                    onClick={() => handleStatusUpdate(log.id, 'skipped')}
                    className="py-3 px-4 bg-slate-50 text-slate-600 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-slate-100 transition-colors"
                  >
                    Skip / Failed
                  </button>
                  <button 
                    onClick={() => handleStatusUpdate(log.id, 'delivered')}
                    className="py-3 px-4 bg-indigo-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-100"
                  >
                    Mark Delivered
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2 py-2 text-xs font-bold text-slate-400">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Already {log.status} at {new Date(log.delivery_time!).toLocaleTimeString()}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
